import { useQuery } from '@tanstack/react-query';
import React from 'react';
import QualAPI from 'utils/QualAPI';
import { normalizeId } from '../utils/idNormalization';

// Define query keys for descriptors
export const descriptorKeys = {
  all: ['descriptors'],
  lists: () => [...descriptorKeys.all, 'list'],
  list: (projectId) => [...descriptorKeys.lists(), normalizeId(projectId)],
  transcript: (transcriptId) => [...descriptorKeys.all, 'transcript', normalizeId(transcriptId)],
  selections: (transcriptId) => ['selections', normalizeId(transcriptId)],
};

/**
 * Hook to fetch descriptors for a project
 * @param {string|number} projectId - The ID of the project to fetch descriptors for
 * @returns {Object} The query result containing descriptors data, loading state, and error
 */
export function useDescriptors(projectId) {
  const normalizedProjectId = normalizeId(projectId);
  
  return useQuery({
    queryKey: descriptorKeys.list(normalizedProjectId),
    queryFn: () => QualAPI.fetchDescriptors(projectId)
      .then(response => response.data),
    enabled: !!projectId,
  });
}

/**
 * Hook to fetch selections for a transcript
 * @param {string|number} transcriptId - The ID of the transcript to fetch selections for
 * @returns {Object} The query result containing selections data, loading state, and error
 */
export function useSelections(transcriptId) {
  const normalizedTranscriptId = normalizeId(transcriptId);
  
  return useQuery({
    queryKey: descriptorKeys.selections(normalizedTranscriptId),
    queryFn: () => QualAPI.fetchSelections(transcriptId)
      .then(response => response.data),
    enabled: !!transcriptId,
  });
}

/**
 * Hook to fetch transcript descriptors with selections
 * This combines descriptors and selections into a single, more usable format
 * @param {string|number} transcriptId - The ID of the transcript
 * @param {string|number} projectId - The ID of the project
 * @returns {Object} The combined transcript descriptors with selections
 */
export function useTranscriptDescriptors(transcriptId, projectId) {
  // Fetch descriptors for the project
  const { 
    data: descriptorsData, 
    isLoading: descriptorsLoading,
    error: descriptorsError
  } = useDescriptors(projectId);
  
  // Fetch selections for the transcript
  const { 
    data: selectionsData, 
    isLoading: selectionsLoading,
    error: selectionsError
  } = useSelections(transcriptId);
  
  // Process and combine the data
  const processedData = React.useMemo(() => {
    if (!descriptorsData || !selectionsData) return [];
    
    // Get descriptors and mark selected choices
    const descriptors = descriptorsData.descriptors || [];
    const selections = selectionsData.selections || [];
    
    // Create a map of descriptor IDs to their selections
    const descriptorSelections = {};
    
    // Group selections by multi_choice_descriptor_id
    selections.forEach(selection => {
      const descriptorId = selection.multi_choice_descriptor_id;
      if (!descriptorSelections[descriptorId]) {
        descriptorSelections[descriptorId] = [];
      }
      descriptorSelections[descriptorId].push(selection);
    });
    
    // Extract selected choice IDs
    const selectedChoiceIds = selections.map(selection => selection.multi_choice_id);
    
    // Mark choices as selected
    return descriptors.map(descriptor => {
      // Clone the descriptor to avoid mutating the query cache
      const processedDescriptor = { ...descriptor };
      
      // Add the selections for this descriptor
      processedDescriptor.selections = descriptorSelections[descriptor.id] || [];
      
      // Mark choices as selected and find the selected choice
      if (processedDescriptor.multi_choices) {
        let selectedChoice = null;
        
        processedDescriptor.multi_choices = processedDescriptor.multi_choices.map(choice => {
          const isSelected = selectedChoiceIds.includes(choice.id);
          
          // Remember the selected choice
          if (isSelected) {
            selectedChoice = { ...choice };
          }
          
          return {
            ...choice,
            selected: isSelected
          };
        });
        
        // Set the selected_multi_choice property expected by the component
        if (selectedChoice) {
          processedDescriptor.selected_multi_choice = selectedChoice;
        }
      }
      
      return processedDescriptor;
    });
  }, [descriptorsData, selectionsData]);
  
  return {
    descriptors: processedData,
    isLoading: descriptorsLoading || selectionsLoading,
    error: descriptorsError || selectionsError
  };
}
